import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ImageUpload, IMAGE_FORMATS } from '../components/ui/ImageUpload';
import { ChevronRight, User, AlertCircle, Check, Clock, Info } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function EditProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, getAuthHeaders, loadUser } = useAuthStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState('es');
  const [avatar, setAvatar] = useState(null);
  const [originalAvatar, setOriginalAvatar] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadProfile();
  }, [user, navigate]);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/profile`, {
        headers: getAuthHeaders()
      });
      const data = response.data;
      setProfile(data);
      setName(data.name || '');
      setPhone(data.phone || '');
      setLanguage(data.preferred_language || 'es');
      setAvatar(data.avatar || null);
      setOriginalAvatar(data.avatar || null);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
    setIsLoading(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage('');
    
    try {
      const updateData = {
        name,
        phone: phone || null,
        preferred_language: language,
      };
      
      // Only include avatar if it changed
      const avatarChanged = avatar !== originalAvatar;
      if (avatarChanged) {
        updateData.avatar = avatar;
      }
      
      const response = await axios.put(
        `${API_URL}/api/auth/profile`,
        updateData,
        { headers: getAuthHeaders() }
      );
      
      // Reload user in store
      await loadUser();
      
      if (response.data.avatar_pending_approval) {
        setSuccessMessage('Profile updated! Your new photo is pending admin approval.');
      } else {
        setSuccessMessage('Profile updated successfully!');
      }
      
      // Reload profile to get latest status
      await loadProfile();
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Error updating profile');
    }
    setIsSaving(false);
  };

  const getAvatarStatusBadge = () => {
    if (!profile) return null;
    
    const status = profile.avatar_status;
    if (status === 'pending') {
      return (
        <Badge className="bg-yellow-500 text-white flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Photo Pending Approval
        </Badge>
      );
    } else if (status === 'rejected') {
      return (
        <Badge className="bg-red-500 text-white flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Photo Rejected
        </Badge>
      );
    } else if (status === 'approved' && profile.avatar) {
      return (
        <Badge className="bg-green-500 text-white flex items-center gap-1">
          <Check className="w-3 h-3" />
          Photo Approved
        </Badge>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#D4A574', borderTopColor: 'transparent' }} />
          <p className="text-secondary">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-8 animate-fade-in" data-testid="edit-profile-page">
      <button onClick={() => navigate('/profile')} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> Back to Profile
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">Edit Profile</h1>
      
      {/* Success Message */}
      {successMessage && (
        <div className="flex gap-3 p-4 rounded-xl mb-6 bg-green-50 border border-green-200">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}
      
      {/* Avatar Status Info */}
      {profile?.avatar_status === 'pending' && (
        <div className="flex gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#FEF3C7' }}>
          <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 font-medium">Photo Pending Approval</p>
            <p className="text-sm text-yellow-700">Your new profile photo is waiting for admin review. Your current photo will remain visible until the new one is approved.</p>
          </div>
        </div>
      )}
      
      {profile?.avatar_status === 'rejected' && (
        <div className="flex gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#FEE2E2' }}>
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-800 font-medium">Photo Was Rejected</p>
            <p className="text-sm text-red-700">Your submitted photo was not approved. Please upload a different photo that follows our guidelines.</p>
          </div>
        </div>
      )}
      
      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* Profile Photo */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-primary">Profile Photo</label>
            {getAvatarStatusBadge()}
          </div>
          
          {/* Show pending avatar preview if exists */}
          {profile?.pending_avatar && profile?.avatar_status === 'pending' && (
            <div className="mb-4 p-3 bg-yellow-50 rounded-xl">
              <p className="text-xs text-yellow-700 mb-2">Pending approval:</p>
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-yellow-400">
                <img src={profile.pending_avatar} alt="Pending" className="w-full h-full object-cover" />
              </div>
            </div>
          )}
          
          <ImageUpload
            image={avatar}
            onImageChange={setAvatar}
            aspectRatio={IMAGE_FORMATS.PROFILE.aspectRatio}
            maxSize={IMAGE_FORMATS.PROFILE.maxSize}
            shape="circle"
            placeholder="Add your photo"
          />
          
          {avatar !== originalAvatar && avatar && (
            <div className="flex gap-2 items-center mt-2 p-2 bg-blue-50 rounded-lg">
              <Info className="w-4 h-4 text-blue-500" />
              <p className="text-xs text-blue-700">New photo will require admin approval</p>
            </div>
          )}
        </div>
        
        {/* Name */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Name *</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            required
            data-testid="edit-name"
          />
        </div>
        
        {/* Email (read-only) */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Email</label>
          <Input
            value={profile?.email || ''}
            disabled
            className="bg-gray-50 text-secondary"
            data-testid="edit-email"
          />
          <p className="text-xs text-secondary mt-1">Email cannot be changed</p>
        </div>
        
        {/* Phone */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Phone</label>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+34 612 345 678"
            data-testid="edit-phone"
          />
        </div>
        
        {/* Language */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Preferred Language</label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger data-testid="edit-language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="it">Italiano</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Role Info */}
        <div className="pt-4 border-t">
          <label className="block text-sm font-semibold text-primary mb-2">Account Type</label>
          <Badge variant="outline" className="text-sm">
            {profile?.role === 'admin' ? 'Administrator' : 
             profile?.role === 'teacher' ? 'Teacher' :
             profile?.role === 'school' ? 'School' : 'User'}
          </Badge>
        </div>
        
        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={isSaving || !name.trim()}
          className="w-full bg-gold hover:bg-gold/90 text-white py-3"
          style={{ backgroundColor: '#D4A574' }}
          data-testid="save-profile-btn"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

export default EditProfilePage;
